import React, { useState, useEffect } from 'react';
import CategorizedSkillsInput from './components/CategorizedSkillsInput';
import ExperiencesInput from './components/ExperiencesInput';

const ScienceTeacherResumeForm = ({ 
  resumeData, 
  onUpdate, 
  validationErrors = {}, 
  onFieldFocus, 
  onFieldBlur 
}) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    location: '',
    linkedin: '',
    professional_summary: '',
    skills: {
      science_subjects: [],
      teaching_methods: [],
      laboratory_skills: [],
      technology_tools: []
    },
    relevant_experience: [],
    education: [],
    certifications: [],
    projects: [],
    achievements: []
  });

  useEffect(() => {
    if (resumeData) {
      setFormData(resumeData);
    }
  }, [resumeData]);

  const handleInputChange = (field, value) => {
    const newData = { ...formData };
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      newData[parent] = { ...newData[parent], [child]: value };
    } else {
      newData[field] = value;
    }
    setFormData(newData);
    onUpdate(newData);
  };

  const handleArrayChange = (field, index, value) => {
    const newData = { ...formData };
    newData[field][index] = value;
    setFormData(newData);
    onUpdate(newData);
  };

  const addArrayItem = (field) => {
    const newData = { ...formData };
    if (field === 'relevant_experience') {
      newData[field].push({
        position: '',
        company: '',
        location: '',
        start_date: '',
        end_date: '',
        description_points: ['']
      });
    } else if (field === 'education') {
      newData[field].push({
        degree: '',
        institute: '',
        field_of_study: '',
        location: '',
        start_year: '',
        end_year: '',
        cgpa: ''
      });
    } else if (field === 'projects') {
      newData[field].push({
        title: '',
        description: '',
        technologies: '',
        link: ''
      });
    } else if (field === 'achievements') {
      newData[field].push('');
    } else if (field === 'certifications') {
      newData[field].push('');
    }
    setFormData(newData);
    onUpdate(newData);
  };

  const removeArrayItem = (field, index) => {
    const newData = { ...formData };
    newData[field].splice(index, 1);
    setFormData(newData);
    onUpdate(newData);
  };

  const handleSkillsChange = (updatedSkills) => {
    handleInputChange('skills', updatedSkills);
  };

  const skillCategories = [
    {
      key: 'science_subjects',
      label: 'Science Subjects',
      placeholder: 'e.g., Biology, Chemistry, Physics, Earth Science'
    },
    {
      key: 'teaching_methods',
      label: 'Teaching Methods',
      placeholder: 'e.g., Inquiry-based Learning, Project-based Learning, Hands-on Experiments'
    },
    {
      key: 'laboratory_skills',
      label: 'Laboratory Skills',
      placeholder: 'e.g., Safety Protocols, Equipment Operation, Experiment Design'
    },
    {
      key: 'technology_tools',
      label: 'Technology Tools',
      placeholder: 'e.g., Smart Boards, Lab Equipment, Educational Software'
    }
  ];

  return (
    <div className="resume-form science-teacher-form">
      <div className="form-section">
        <h3>Personal Information</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Full Name *</label>
            <input
              type="text"
              value={formData.full_name || ''}
              onChange={(e) => handleInputChange('full_name', e.target.value)}
              onFocus={() => onFieldFocus('full_name')}
              onBlur={() => onFieldBlur('full_name')}
              className={validationErrors.full_name ? 'error' : ''}
            />
            {validationErrors.full_name && <span className="error-message">{validationErrors.full_name}</span>}
          </div>
          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onFocus={() => onFieldFocus('email')}
              onBlur={() => onFieldBlur('email')}
              className={validationErrors.email ? 'error' : ''}
            />
            {validationErrors.email && <span className="error-message">{validationErrors.email}</span>}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel"
              value={formData.phone || ''}
              onChange={(e) => handleInputChange('phone', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              value={formData.location || ''}
              onChange={(e) => handleInputChange('location', e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label>LinkedIn</label>
          <input
            type="url"
            value={formData.linkedin || ''}
            onChange={(e) => handleInputChange('linkedin', e.target.value)}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Professional Summary</h3>
        <div className="form-group">
          <label>Summary *</label>
          <textarea
            value={formData.professional_summary || ''}
            onChange={(e) => handleInputChange('professional_summary', e.target.value)}
            onFocus={() => onFieldFocus('professional_summary')}
            onBlur={() => onFieldBlur('professional_summary')}
            className={validationErrors.professional_summary ? 'error' : ''}
            rows="4"
            placeholder="Write a compelling professional summary that highlights your science teaching expertise and achievements..."
          />
          {validationErrors.professional_summary && <span className="error-message">{validationErrors.professional_summary}</span>}
        </div>
      </div>

      <div className="form-section">
        <h3>Skills</h3>
        <CategorizedSkillsInput
          skills={formData.skills || {}}
          onChange={handleSkillsChange}
          categories={skillCategories}
        />
      </div>

      <div className="form-section">
        <ExperiencesInput
          experiences={formData.relevant_experience}
          onChange={(updated) => {
            const newData = { ...formData, relevant_experience: updated };
            setFormData(newData);
            onUpdate(newData);
          }}
          errors={validationErrors.relevant_experience}
          onFieldFocus={onFieldFocus}
          onFieldBlur={onFieldBlur}
          title="Teaching Experience"
          addLabel="Add Teaching Experience"
          companyLabel="School/Institution"
          positionPlaceholder="e.g., Science Teacher"
          companyPlaceholder="e.g., ABC High School"
          locationPlaceholder="e.g., New York, NY"
          startDatePlaceholder="e.g., Aug 2020"
          endDatePlaceholder="e.g., Present"
          pointsLabel="Teaching Responsibilities"
          pointsPlaceholder="Describe your teaching responsibilities and achievements..."
          pointsMaxLength={300}
        />
      </div>

      <div className="form-section">
        <h3>Education</h3>
        {formData.education?.map((edu, index) => (
          <div key={index} className="education-item">
            <div className="form-row">
              <div className="form-group">
                <label>Degree</label>
                <input
                  type="text"
                  value={edu.degree || ''}
                  onChange={(e) => handleArrayChange('education', index, { ...edu, degree: e.target.value })}
                  placeholder="e.g., Master of Education"
                />
              </div>
              <div className="form-group">
                <label>Institution</label>
                <input
                  type="text"
                  value={edu.institute || ''}
                  onChange={(e) => handleArrayChange('education', index, { ...edu, institute: e.target.value })}
                  placeholder="e.g., University Name"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Field of Study</label>
                <input
                  type="text"
                  value={edu.field_of_study || ''}
                  onChange={(e) => handleArrayChange('education', index, { ...edu, field_of_study: e.target.value })}
                  placeholder="e.g., Science Education"
                />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={edu.location || ''}
                  onChange={(e) => handleArrayChange('education', index, { ...edu, location: e.target.value })}
                  placeholder="e.g., New York, NY"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Year</label>
                <input
                  type="text"
                  value={edu.start_year || ''}
                  onChange={(e) => handleArrayChange('education', index, { ...edu, start_year: e.target.value })}
                  placeholder="e.g., 2016"
                />
              </div>
              <div className="form-group">
                <label>End Year</label>
                <input
                  type="text"
                  value={edu.end_year || ''}
                  onChange={(e) => handleArrayChange('education', index, { ...edu, end_year: e.target.value })}
                  placeholder="e.g., 2020"
                />
              </div>
              <div className="form-group">
                <label>CGPA</label>
                <input
                  type="text"
                  value={edu.cgpa || ''}
                  onChange={(e) => handleArrayChange('education', index, { ...edu, cgpa: e.target.value })}
                  placeholder="e.g., 3.8/4.0"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => removeArrayItem('education', index)}
              className="remove-btn"
            >
              Remove Education
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addArrayItem('education')}
          className="add-btn"
        >
          Add Education
        </button>
      </div>

      <div className="form-section">
        <h3>Projects & Activities</h3>
        {formData.projects?.map((project, index) => (
          <div key={index} className="project-item">
            <div className="form-row">
              <div className="form-group">
                <label>Project/Activity Title</label>
                <input
                  type="text"
                  value={project.title || ''}
                  onChange={(e) => handleArrayChange('projects', index, { ...project, title: e.target.value })}
                  placeholder="e.g., Science Fair Coordinator"
                />
              </div>
              <div className="form-group">
                <label>Tools/Technologies</label>
                <input
                  type="text"
                  value={project.technologies || ''}
                  onChange={(e) => handleArrayChange('projects', index, { ...project, technologies: e.target.value })}
                  placeholder="e.g., Lab Equipment, Educational Software"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={project.description || ''}
                onChange={(e) => handleArrayChange('projects', index, { ...project, description: e.target.value })}
                placeholder="Describe the project, your role, and outcomes..."
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>Project Link</label>
              <input
                type="url"
                value={project.link || ''}
                onChange={(e) => handleArrayChange('projects', index, { ...project, link: e.target.value })}
                placeholder="e.g., https://project-url.com"
              />
            </div>
            <button
              type="button"
              onClick={() => removeArrayItem('projects', index)}
              className="remove-btn"
            >
              Remove Project
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addArrayItem('projects')}
          className="add-btn"
        >
          Add Project/Activity
        </button>
      </div>

      <div className="form-section">
        <h3>Certifications</h3>
        {formData.certifications?.map((cert, index) => (
          <div key={index} className="certification-item">
            <input
              type="text"
              value={cert || ''}
              onChange={(e) => handleArrayChange('certifications', index, e.target.value)}
              placeholder="e.g., Teaching License, Science Certification"
            />
            <button
              type="button"
              onClick={() => removeArrayItem('certifications', index)}
              className="remove-btn"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addArrayItem('certifications')}
          className="add-btn"
        >
          Add Certification
        </button>
      </div>

      <div className="form-section">
        <h3>Key Achievements</h3>
        {formData.achievements?.map((achievement, index) => (
          <div key={index} className="achievement-item">
            <input
              type="text"
              value={achievement || ''}
              onChange={(e) => handleArrayChange('achievements', index, e.target.value)}
              placeholder="e.g., Improved student test scores by 30% through innovative teaching methods"
            />
            <button
              type="button"
              onClick={() => removeArrayItem('achievements', index)}
              className="remove-btn"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addArrayItem('achievements')}
          className="add-btn"
        >
          Add Achievement
        </button>
      </div>
    </div>
  );
};

export default ScienceTeacherResumeForm; 
